import { useDeferredValue, useEffect, useState } from "react";
import {
  ListIconWrapper,
  BodyContainer,
  MainButton,
  PrimaryHeading,
  HeaderContainer,
  ListCard,
  RightMainContainer,
  LoadingListCards,
  TemplateNotFound,
  PopOver,
  PopUpListing,
  AddFromTemplateLibrary,
  Loader,
  InputField,
  FiltersList,
} from "commonUI";
import { AddIcon, RolesIcon, CloudDownloadIcon } from "allIcons";
import {
  useAddDefaultRole,
  useAddRole,
  useCheckRoleBeforeDelete,
  useDefaultRoles,
  useDeleteDefaultRole,
  useDeleteRole,
  useRoles,
} from "api/usersModule/roles";
import { useUtilities, isEqual, useSearch, search, omit, parse } from "helpers";
import {
  libraryItemFiltering,
  libraryItemSorting,
} from "filterAndSort/templateLibrary";
import { useUserPermissions } from "hooks/user";
import { t } from "i18next";
import { ModulePrefixes } from "enums";

export default function RolesTab(props) {
  const { web = null } = useSearch();
  const { navigate } = useUtilities();
  const { data: roles, isLoading } = useDefaultRoles();
  const { data: rolesList, isLoading: loadingRoles } = useRoles();
  const { mutate: addDefaultRole, isLoading: isAdding } = useAddDefaultRole();
  const { mutate: removeDefaultRole, isLoading: removingDefaultRole } =
    useDeleteDefaultRole();
  const { mutate: addRoleToWeb, isLoading: addingRoleToWeb } = useAddRole(
    "",
    true
  );
  const { mutate: deleteRole, isLoading: deletingRole } = useDeleteRole();
  const {
    add_new_items_to_site_template,
    add_items_from_template_library,
    remove_items_from_site_template,
    edit_item_details_in_site_template,
  } = useUserPermissions();

  const {
    mutateAsync: checkRoleBeforeDelete,
    isLoading: checkingRoleBeforeDelete,
  } = useCheckRoleBeforeDelete();

  const [searchText, setSearchText] = useState("");
  const [searchResult, setSearchResult] = useState([]);
  const [filterData, setFilterData] = useState({});
  const defferedSearch = useDeferredValue(searchText);

  const [addModal, setAddModal] = useState(false);
  const [addRole, setAddRole] = useState(false);

  useEffect(() => {
    setSearchResult(
      roles?.filter((role) => search(role.role_title, defferedSearch) && !isEqual(role.nick, 'owner') ) ?? []
    );
  }, [defferedSearch, roles]);

  const applySorting = (order, orderBy) => {
    setSearchResult((prevSearchResult) =>
      libraryItemSorting(order, orderBy, prevSearchResult, "role_title")
    );
  };

  const applyFilter = ({
    selectedFilter,
    selectedTags,
    selectedLibrarySource,
    ...data
  }) => {
    setFilterData({
      ...data,
      selectedTags,
      selectedLibrarySource,
      selectedFilter,
    });
    setSearchResult(
      libraryItemFiltering(
        selectedFilter,
        selectedTags,
        selectedLibrarySource,
        roles.filter((_) => !isEqual(_.nick, "owner")),
        web ? "user_role" : "system_status"
      )
    );
  };
  const rolesTitle = roles?.map((role) => role.role_title);

  return (
    <>
      <RightMainContainer>
        <Loader
          show={
            isAdding ||
            removingDefaultRole ||
            addingRoleToWeb ||
            deletingRole ||
            checkingRoleBeforeDelete
          }
          loader="block"
        />
        <HeaderContainer>
          <PrimaryHeading>{t("siteSetting.roles.title")}</PrimaryHeading>
          <div className="relative">
            {add_new_items_to_site_template || add_items_from_template_library ? (
              <MainButton onClick={() => setAddModal(!addModal)}>
                <AddIcon />
                {t("button.add")}
              </MainButton>
            ) : (
              ""
            )}
            <PopOver
              toolTip={{ vertical: "top", right: "30px" }}
              open={addModal}
              onClose={() => {
                setAddModal(false);
                setAddRole(false);
              }}
              style={{
                top: "calc(100% + 10px)",
                right: "0",
                bottom: "auto",
              }}
            >
              {addRole ? (
                <AddFromTemplateLibrary
                  heading={t("siteSetting.roles.addRoleFromUserLibrary")}
                  isLoading={loadingRoles}
                  data={rolesList
                    .filter((ele) =>
                      web
                        ? !rolesTitle.includes(ele.role_title)
                        : !isEqual(ele.default_tem, "1")
                    )
                    .map((item) => ({
                      ...item,
                      title: item.role_title,
                      onClick: () => {
                        if (web) {
                          addRoleToWeb({
                            data: omit(["id"], {
                              ...item,
                              system_default: 0,
                              website_id: web,
                              app_permissions: parse(item.app_permissions),
                              plugin_permissions: parse(
                                item.plugin_permissions
                              ),
                            }),
                          });
                          return;
                        }
                        addDefaultRole({ id: item.id, website_id: web });
                      },
                      icon: (
                        <ListIconWrapper>
                          <RolesIcon fill="white" />
                        </ListIconWrapper>
                      ),
                    }))}
                  emptyTemplate={{
                    label: t("siteSetting.roles.role"),
                    linkLabel: t("siteSetting.roles.role"),
                    link: `/${web ? ModulePrefixes.SITE_SETTINGS : ModulePrefixes.SITE_TEMPLATE}/roles/add${web ? `?web=${web}` : ""}`,
                  }}
                />
              ) : (
                <PopUpListing
                  data={[
                    add_items_from_template_library && {
                      title: t("common.addOption.fromAccount"),
                      icon: <CloudDownloadIcon />,
                      onClick: () => {
                        setAddRole(true);
                      },
                      arrow: true,
                    },
                    add_new_items_to_site_template && {
                      title: t("siteSetting.roles.addOption.createNewRole"),
                      icon: <AddIcon />,
                      onClick: () => {
                        navigate(
                          `/${web ? ModulePrefixes.SITE_SETTINGS : ModulePrefixes.SITE_TEMPLATE}/roles/add${web ? `?web=${web}` : ""}`
                        );
                      },
                    },
                  ].filter((_) => _)}
                />
              )}
            </PopOver>
          </div>
        </HeaderContainer>
        <BodyContainer>
          <InputField
            searchIcon
            placeholder={t("input.search.placeholder")}
            filter
            value={searchText}
            setValue={setSearchText}
            showTags={false}
            filterData={filterData}
            onApplyFilter={applyFilter}
            applySorting={applySorting}
            filterAddTitle={web ? t("siteSetting.roles.filter.userType.title") : t("filter.popup.title")}
            filterTitle={web ? t("siteSetting.roles.filter.userType.title") : t("filter.popup.title")}
            sortOptions={[
              {
                label: t("sort.options.alphabeticalByTitle"),
                value: "alphabetical-by-title",
              },
              {
                label: t("sort.options.dateAdded"),
                value: "date-added",
              },
            ]}
            filterList={
              web
                ? [
                    {
                      id: 1,
                      title: t("siteSetting.roles.filter.userType.option.teamMember"),
                      user_role: "Team Member",
                    },
                    {
                      id: 2,
                      title: t("siteSetting.roles.filter.userType.option.client"),
                      user_role: "Client",
                    },
                  ]
                : [
                    {
                      id: 1,
                      title: t("filter.options.includedByDefault"),
                      system_status: 1,
                    },
                    {
                      id: 2,
                      title: t("filter.options.createdByUser"),
                      system_status: 0,
                    },
                  ]
            }
          />
          <FiltersList filterData={filterData} updateFilter={applyFilter} />
          {isLoading ? (
            <LoadingListCards />
          ) : searchResult.length === 0 ? (
            <TemplateNotFound
              label={t("siteSetting.roles.role")}
              linkLabel={t("siteSetting.roles.role")}
              link={`/${web ? ModulePrefixes.SITE_SETTINGS : ModulePrefixes.SITE_TEMPLATE}/roles/add${web ? `?web=${web}` : ""}`}
            />
          ) : (
            searchResult.map((iner, index) => {
              return (
                <ListCard
                  data={iner.role_title}
                  key={index}
                  index={index}
                  strictLink={true}
                  link={
                    edit_item_details_in_site_template
                      ? `/${props.from ?? `${web ? ModulePrefixes.SITE_SETTINGS : ModulePrefixes.SITE_TEMPLATE}`}/roles/${iner.id}`
                      : ""
                  }
                  editIcon={edit_item_details_in_site_template}
                  icon={
                    <ListIconWrapper>
                      <RolesIcon fill="white" />
                    </ListIconWrapper>
                  }
                  deleteIcon={true}
                  crossIcon={remove_items_from_site_template}
                  onCross={async () => {
                    const result = await checkRoleBeforeDelete(iner.id).then(
                      (res) => {
                        if (
                          isEqual(res.data.for_temp, 0) &&
                          isEqual(res.data.for_temp, 0)
                        ) {
                          if (web) {
                            deleteRole(iner.id);
                            return true;
                          }
                          removeDefaultRole({
                            id: iner.id,
                            website_id: web,
                          });
                          return true;
                        } else {
                          return false;
                        }
                      }
                    );
                    return result;
                  }}
                  showModalonCross
                  deleteTitle={web ? t("siteSetting.roles.confirmDelete.forSite.title") : t("siteSetting.roles.confirmDelete.title")}
                  deleteButtonText={web ? t("common.popup.button.confirmDelete") : t("common.popup.button.confirm")}
                  showDeleteResult
                  deleteMessage={
                    web
                      ? t("siteSetting.roles.confirmDelete.forSite.message")
                      : t("siteSetting.roles.confirmDelete.message")
                  }
                  deleteResponseMessage={
                    web
                      ? t("siteSetting.roles.deleteResponse.forSite.message")
                      : t("siteSetting.roles.deleteResponse.message")
                  }
                  deleteResponseTitle={t("siteSetting.roles.deleteResponse.title")}
                />
              );
            })
          )}
        </BodyContainer>
      </RightMainContainer>
    </>
  );
}
